import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { timeout, restartableTask } from 'ember-concurrency';
import config from 'uplisting-frontend/config/environment';
import { storage, StorageKey } from 'uplisting-frontend/utils/storage';

const GATE_NAME = 'maintenance_mode';
const BYPASS_PARAM = 'maintenance_bypass';

export default class MaintenanceService extends Service {
  @service featureFlags!: Services['feature-flags'];

  POLL_INTERVAL = 60_000;

  @tracked isDown = false;

  get isBypassed(): boolean {
    return this.#storage.get<boolean>(StorageKey.MAINTENANCE_BYPASS) === true;
  }

  async check(): Promise<boolean> {
    this.#readBypassParam();

    if (this.isBypassed) {
      this.isDown = false;

      return false;
    }

    await this.featureFlags.initialize();

    this.isDown = this.featureFlags.liveGateEnabled(GATE_NAME);

    return this.isDown;
  }

  // Both directions matter: tabs already open have to enter maintenance, and
  // tabs stuck on the page have to leave it without the user knowing to reload.
  public poll = restartableTask(async () => {
    // `timeout` holds the test settled-state open, so polling is production
    // only (same reason as new-version-notifier).
    if (!config.IS_PROD || this.isBypassed) {
      return;
    }

    for (;;) {
      await timeout(this.POLL_INTERVAL);

      await this.featureFlags.refresh();

      this.isDown = this.featureFlags.liveGateEnabled(GATE_NAME);
    }
  });

  // Read straight off `window.location` rather than as a route query param:
  // this runs before the router has parsed anything, and the bypass has to hold
  // for the whole session rather than only the URL that carried it.
  #readBypassParam(): void {
    const value = new window.URLSearchParams(window.location.search).get(
      BYPASS_PARAM,
    );

    if (value === '1') {
      this.#storage.set(StorageKey.MAINTENANCE_BYPASS, true);
    } else if (value === '0') {
      this.#storage.remove(StorageKey.MAINTENANCE_BYPASS);
    }
  }

  get #storage() {
    return storage.withStorage(window.sessionStorage);
  }
}

declare module '@ember/service' {
  interface Registry {
    maintenance: MaintenanceService;
  }
}
